import React from 'react';
import { Box, Typography, Chip } from '@mui/material';
import { groupChannelTotals, GROUP_ORDER, groupLabel } from './messageTypes.js';

/**
 * Composition breakdown for an insights payload: appointments split into walk-in vs scheduled,
 * and the sent SMS/email broken down by type (booked / reminder / link sent / …), with the
 * SMS vs email split per type. Works for both org insights (fields at top level) and platform
 * insights (fields under `totals`).
 */
export default function InsightsBreakdown({ data }) {
  if (!data) return null;
  const totals = data.totals || data;
  const scheduled = data.appointments_scheduled ?? totals.appointments_scheduled;
  const walkIn = data.appointments_walk_in ?? totals.appointments_walk_in;
  const hasApptSplit = scheduled != null || walkIn != null;

  // Per-group SMS/email split (checked_in + checked_out merge into "Status changed"). Falls back
  // to the combined per-type counts when the channel breakdown isn't present.
  const byChannel = data.messages_by_type_channel;
  let entries = groupChannelTotals(byChannel);
  if (entries.length === 0 && data.messages_by_type) {
    const byType = data.messages_by_type;
    entries = GROUP_ORDER
      .map((g) => ({ group: g, label: groupLabel(g), total: byType[g] || 0, sms: 0, email: 0 }))
      .filter((e) => e.total > 0);
  }

  if (!hasApptSplit && entries.length === 0) return null;

  return (
    <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
      {hasApptSplit && (
        <Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Appointments</Typography>
          <Box sx={{ display: 'flex', gap: 0.75, mt: 0.5 }}>
            <Chip size="small" variant="outlined" label={`${walkIn ?? 0} walk-in`} />
            <Chip size="small" variant="outlined" label={`${scheduled ?? 0} scheduled`} />
          </Box>
        </Box>
      )}
      {entries.length > 0 && (
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>Messages by type</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.5 }}>
            {entries.map((e) => (
              <Chip key={e.group} size="small" variant="outlined"
                label={byChannel
                  ? `${e.label} · SMS ${e.sms} · Email ${e.email}`
                  : `${e.label}: ${e.total}`} />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}
